import { LOCAL_STORE_NAMESPACE } from '@streamlain/doc-publication-contract';
import { type IDBPDatabase, openDB } from 'idb';

import type { LocalAccountAuthority } from '../../local-data/key-provider';
import type { DocStorageSchema } from './schema';

/** Monotonic: a newer epoch, or the same epoch turning logout-pending. */
export async function raiseMirror(
  db: IDBPDatabase<DocStorageSchema>,
  accountId: string,
  authority: LocalAccountAuthority
) {
  const tx = db.transaction('authState', 'readwrite');
  const done = tx.done;
  void done.catch(() => {});
  const stored = await tx.store.get(accountId);
  if (
    !stored ||
    stored.authEpoch < authority.authEpoch ||
    (stored.authEpoch === authority.authEpoch &&
      authority.logoutPending &&
      !stored.logoutPending)
  )
    await tx.store.put({ accountId, ...authority });
  await done;
}

/**
 * Raises the authState mirror of every IndexedDB database of the account
 * (IDBConnection.dbName ends with `@<namespace>:<accountId>`), so every later
 * connection there opens locked (LocalDataKeyProvider.connect) and plain data
 * fails closed in assertAuth. LocalAccountKeyStore logout
 * and login complete only after it; without a database listing there is no
 * way to reach them.
 */
export async function raiseAccountMirrors(
  accountId: string,
  authority: LocalAccountAuthority
) {
  // Not a LOCKED code: a failed raise is no lost login CAS (LocalAccountKeys).
  if (typeof indexedDB.databases !== 'function')
    throw new Error('IndexedDB database listing unavailable');
  const suffix = `@${LOCAL_STORE_NAMESPACE}:${accountId}`;
  const names = (await indexedDB.databases())
    .map(info => info.name)
    .filter((name): name is string => !!name?.endsWith(suffix));
  for (const name of names) {
    let deleted = false;
    // Without a version the database opens as it is; a name deleted since
    // the listing would be created empty, so that upgrade is aborted.
    const db = await openDB<DocStorageSchema>(name, undefined, {
      upgrade(db, oldVersion, _newVersion, tx) {
        if (oldVersion !== 0) return;
        deleted = true;
        void tx.done.catch(() => {});
        tx.abort();
        // The abort closes the connection; closing it here as well keeps a
        // later open of the name from waiting on it where that is missed.
        db.close();
      },
    }).catch(error => {
      if (deleted) return null;
      throw error;
    });
    if (!db) continue;
    try {
      if (!db.objectStoreNames.contains('authState'))
        throw new Error(`IndexedDB database ${name} has no auth mirror`);
      await raiseMirror(db, accountId, authority);
    } finally {
      db.close();
    }
  }
}
